import React, { useEffect, useState, useRef } from 'react'
import { Dialog, DialogContent, IconButton, Box, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import DownloadIcon from '@mui/icons-material/Download'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import { motion, AnimatePresence } from 'framer-motion'

export default function PortfolioPopUp ({
  open, onClose, initialSrc, images
}) {
  const [index, setIndex] = useState(() => {
    return initialSrc ? images.findIndex(i => i.src === initialSrc) : 0
  });
  const [zoomed, setZoomed] = useState(false)
  const touchStartX = useRef(null)


  useEffect(() => {
    if (!initialSrc) return
    const idx = images.findIndex(i => i.src === initialSrc)
    setIndex(idx >= 0 ? idx : 0)
    setZoomed(false)
  }, [initialSrc, images]);


  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') setIndex(i => (i - 1 + images.length) % images.length)
      if (e.key === 'ArrowRight') setIndex(i => (i + 1) % images.length)
      if (e.key === '+' || e.key === '=') setZoomed(z => !z)
    };
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, images.length, onClose]);

  const prev = () => setIndex(i => (i - 1 + images.length) % images.length)
  const next = () => setIndex(i => (i + 1) % images.length)

  const onTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return
    const endX = e.changedTouches?.[0]?.clientX ?? null
    if (endX == null) return
    const dx = endX - touchStartX.current
    if (Math.abs(dx) > 40) {
      if (dx > 0) prev()
      else next()
    }
    touchStartX.current = null
  };

  const current = images[index]

  const downloadCurrent = () => {
    if (!current?.src) return

    const ext = current.name?.split('.').pop() || 'jpg'

    const randomName = `imseen_${Math.random().toString(36).slice(2, 10)}.${ext}`

    const a = document.createElement('a')
    a.href = current.src
    a.download = randomName
    document.body.appendChild(a)
    a.click()
    a.remove()
  };

  return (<Dialog
    open={open}
    onClose={onClose}
    maxWidth="xl"
    fullWidth
    PaperProps={{
      sx: {
        background: 'transparent', boxShadow: 'none', overflow: 'hidden'
      }
    }}
    BackdropProps={{
      sx: {
        backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.6)'
      }
    }}
   >
     <DialogContent sx={{ p: 0, position: 'relative', background: 'transparent' }}>
       <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 30, display: 'flex', gap: 1 }}>
         <Tooltip title="Download">
           <IconButton onClick={downloadCurrent} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' }}>
             <DownloadIcon/>
           </IconButton>
         </Tooltip>

         <Tooltip title={zoomed ? 'Zoom out' : 'Zoom in'}>
           <IconButton onClick={() => setZoomed(z => !z)} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' }}>
             {zoomed ? <ZoomOutIcon/> : <ZoomInIcon/>}
           </IconButton>
         </Tooltip>

         <IconButton onClick={onClose} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' }}>
           <CloseIcon/>
         </IconButton>
       </Box>
       <IconButton
        onClick={prev}
        sx={{
          position:  'absolute',
          left:      8,
          top:       '50%',
          transform: 'translateY(-50%)',
          zIndex:    25,
          color:     '#fff',
          bgcolor:   'rgba(0,0,0,0.35)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.45)' }
        }}
        aria-label="previous"
       >
         <ArrowBackIosNewIcon/>
       </IconButton>

       <IconButton
        onClick={next}
        sx={{
          position:  'absolute',
          right:     8,
          top:       '50%',
          transform: 'translateY(-50%)',
          zIndex:    25,
          color:     '#fff',
          bgcolor:   'rgba(0,0,0,0.35)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.45)' }
        }}
        aria-label="next"
       >
         <ArrowForwardIosIcon/>
       </IconButton>
       <Box
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        sx={{
          width:                              '100%',
          height:                             { xs: '70vh', md: '85vh' },
          display:                            'flex',
          alignItems:                         'center',
          justifyContent: 'center',
          position:                           'relative',
          zIndex:                             20,
          px:                                 { xs: 2, md: 6 }
        }}
       >
         <AnimatePresence mode="wait">
           {current && (<motion.img
             key={current.src}
             src={current.src}
             alt={current.name}
             initial={{ opacity: 0, scale: 0.98 }}
             animate={{ opacity: 1, scale: zoomed ? 1.12 : 1 }}
             exit={{ opacity: 0, scale: 0.98 }}
             transition={{ duration: 0.45, ease: 'easeOut' }}
             style={{
               maxWidth:     '100%',
               maxHeight:    '100%',
               objectFit:    'contain',
               borderRadius: 8,
               boxShadow:    '0 30px 80px rgba(0,0,0,0.6)',
               cursor:       zoomed ? 'grab' : 'auto'
             }}
             draggable={false}
            />)}
         </AnimatePresence>
       </Box>

       <Box sx={{ px: { xs: 2, md: 6 }, pb: { xs: 3, md: 4 }, pt: 2, zIndex: 20 }}>
         <Box sx={{
           display: 'flex', gap: 1, overflowX: 'auto', py: 1, px: 1, alignItems: 'center', justifyContent: 'center'
         }}>
           {images.map((img, i) => {
             const active = i === index
             return (<Box
               key={img.src}
               onClick={() => setIndex(i)}
               component="img"
               src={img.src}
               alt={img.name}
               loading="lazy"
               sx={{
                 width:                   { xs: 64, md: 84 },
                 height:                  { xs: 44, md: 56 },
                 objectFit:               'cover',
                 borderRadius: 1,
                 border:                  active
                                           ? '2px solid rgba(255,255,255,0.95)'
                                           : '1px solid rgba(255,255,255,0.12)',
                 opacity:                 active ? 1 : 0.7,
                 transform:               active ? 'scale(1.03)' : 'scale(1)',
                 transition:              'transform 0.18s, border 0.18s, opacity 0.18s',
                 cursor:                  'pointer',
                 boxShadow:               active ? '0 8px 30px rgba(0,0,0,0.6)' : 'none'
               }}
              />);
           })}
         </Box>
       </Box>
     </DialogContent>
   </Dialog>);
}
